import { findLast, getOr, indexOf, isNull, set } from 'lodash/fp';
import { all, call, delay, fork, put, select, takeEvery, takeLeading } from 'redux-saga/effects';

import { getFewShotDocumentById } from '../../../../services/fewShotDocument';
import { dismissAllNotifications, dismissNotification } from '../../../../services/notifications';
import { fetchFewShotDocumentsStarted, openFewShotDocumentSuccessful } from '../../../admin/ai/few-shot/store/actions';
import { getFewShotDocument, getFewShotDocuments, getFewShotDocumentUrl, getTablePageNumber } from '../../../admin/ai/few-shot/store/selectors';
import { FewShotDocumentQuery, FewShotDocumentTable, MLFewShotDocument } from '../../../admin/ai/few-shot/store/types';
import { amendmentExtensiveProgressSteps, extensiveProgressSteps, initialProgressSteps, opinionProgressSteps, setFewShotProgressString, setOpinionProgressString, setProgressString, setTestDocumentProgressString } from '../../../constants/mlProgress';
import { openTimelineInstanceStarted, setIsQueryingDocumentWithAI } from '../../../datasets/instances/store/actions';
import { getDatasetInstance, getDatasetInstanceTimeline, getInstanceDocumentId } from '../../../datasets/instances/store/selectors';
import { DatasetInstanceTimeline, Instance } from '../../../datasets/instances/store/types';
import { openOpinionInstanceStarted } from '../../../opinions/instances/store/actions';
import { getCurrentInstanceOpinionId, getOpinionInstanceTimeline } from '../../../opinions/instances/store/selectors';
import { TimelineOpinionInstance } from '../../../opinions/instances/store/types';
import {
    dismissAllNotificationsFailed,
    dismissAllNotificationsSuccessful,
    dismissNotificationFailed,
    dismissNotificationStarted,
    dismissNotificationSuccessful,
    removeActiveFewShotDocument,
    removeActiveMLDocument,
    removeActiveMLOpinion,
    removeTestMLDocument,
    setActiveFewShotDocuments,
    setActiveMLDocuments,
    setActiveMLOpinions,
    triggerProgressActiveFewShotDocuments,
    triggerProgressActiveMLDocuments,
    triggerProgressActiveMLOpinions,
    triggerProgressTestMLDocuments,
    updatePercentageActiveFewShotDocument,
    updatePercentageActiveMLDocument,
    updatePercentageActiveMLOpinion,
    updatePercentageTestMLDocument
} from './actions';
import { getActiveFewShotDocuments, getActiveMLDocuments, getActiveMLOpinions, getTestMLDocuments } from './selectors';
import { ActiveFewShotDocument, ActiveMLDocument, ActiveMLDocumentType, ActiveMLOpinion, NotificationActionTypes, TestMLDocument } from './types';

const checkShouldIncrementProgress = (progressSteps: number[], progress: number): boolean => {
    let baseProgress = findLast(progressStep => progress >= progressStep, progressSteps);
    let indexOfBaseProgress = indexOf(baseProgress, progressSteps);
    let maxProgress = (progressSteps[indexOfBaseProgress + 1] - 1) || 99;
    let shouldIncrementProgress = ![0, maxProgress, 100].includes(progress);
    return shouldIncrementProgress;
};

const getProgressSteps = (activeMLDocumentType: ActiveMLDocumentType) => {
    let progressSteps = initialProgressSteps;
    switch (activeMLDocumentType) {
        case ActiveMLDocumentType.EXTENSIVE_EXTRACTION:
            return extensiveProgressSteps;
        case ActiveMLDocumentType.AMENDMENT_QUERY_EXTRACTION:
            return amendmentExtensiveProgressSteps;
        default:
            return progressSteps;
    }
};

export function* attemptDismissNotification({ payload }: ReturnType<typeof dismissNotificationStarted>) {
    try {
        yield call(dismissNotification, { notificationId: payload });
        yield put(dismissNotificationSuccessful());
    } catch (e) {
        yield put(dismissNotificationFailed((e as Error).message));
    }
}

function* dismissNotificationWatcher() {
    yield takeLeading(NotificationActionTypes.DISMISS_NOTIFICATION_STARTED, attemptDismissNotification);
}

export function* attemptDismissAllNotifications() {
    try {
        yield call(dismissAllNotifications);
        yield put(dismissAllNotificationsSuccessful());
    } catch (e) {
        yield put(dismissAllNotificationsFailed((e as Error).message));
    }
}

function* dismissAllNotificationsWatcher() {
    yield takeLeading(NotificationActionTypes.DISMISS_ALL_NOTIFICATIONS_STARTED, attemptDismissAllNotifications);
}

export function* attemptUpdateActiveMLDocumentProgress({ payload }: ReturnType<typeof triggerProgressActiveMLDocuments>) {
    const { directFromSocket, activeMLDocument } = payload;
    let activeDocument = activeMLDocument;
    const initialMLDocuments: ActiveMLDocument[] = yield select(getActiveMLDocuments);
    const currentProgress = getOr(1, 'progress', initialMLDocuments.find(({ documentId }) => documentId === activeDocument.documentId));
    if (((activeDocument.progress > currentProgress) || activeDocument.progress === 0) && currentProgress !== 0) {
        if (directFromSocket) {
            activeDocument = setProgressString(activeDocument);
        }
        yield put(updatePercentageActiveMLDocument(activeDocument));
        if ([100, 0].includes(activeDocument.progress)) {
            yield delay(5000);
            yield put(removeActiveMLDocument(activeDocument.documentId));
        } else {
            const progressSteps = getProgressSteps(activeMLDocument.activeMLDocumentType);
            const updatedMLDocuments: ActiveMLDocument[] = yield select(getActiveMLDocuments);
            const progress = updatedMLDocuments.find(({ documentId }) => documentId === activeDocument.documentId)?.progress || 0;
            const shouldIncrementProgress = checkShouldIncrementProgress(progressSteps, progress);
            if (shouldIncrementProgress) {
                const randomIncrement = Math.floor((Math.random() + 1) * 1000);
                yield delay(randomIncrement);
                yield put(triggerProgressActiveMLDocuments(set('progress', progress + 1, activeDocument)));
            }
        }
    }
}

function* activeMLDocumentProgressWatcher() {
    yield takeEvery(NotificationActionTypes.TRIGGER_PROGRESS_PERCENTAGE_ACTIVE_ML_DOCUMENT, attemptUpdateActiveMLDocumentProgress);
}

export function* attemptTriggerAllActiveMLDocumentProgress({ payload }: ReturnType<typeof setActiveMLDocuments>) {
    const directFromSocket = true;
    for (const activeMLDocument of payload) {
        yield put(triggerProgressActiveMLDocuments(set('progress', activeMLDocument.progress + 1, activeMLDocument), directFromSocket));
    }
}

function* setAllActiveMLDocumentProgressWatcher() {
    yield takeEvery(NotificationActionTypes.SET_ACTIVE_ML_DOCUMENTS, attemptTriggerAllActiveMLDocumentProgress);
}

export function* attemptUpdateActiveMLOpinionProgress({ payload }: ReturnType<typeof triggerProgressActiveMLOpinions>) {
    const { directFromSocket, activeMLOpinion } = payload;
    let activeOpinion = activeMLOpinion;
    const initialMLOpinions: ActiveMLOpinion[] = yield select(getActiveMLOpinions);
    const currentProgress = getOr(1, 'progress', initialMLOpinions.find(({ opinionId }) => opinionId === activeOpinion.opinionId));
    if (((activeOpinion.progress > currentProgress) || activeOpinion.progress === 0) && currentProgress !== 0) {
        if (directFromSocket) {
            activeOpinion = setOpinionProgressString(activeOpinion);
        }
        yield put(updatePercentageActiveMLOpinion(activeOpinion));
        if ([100, 0].includes(activeOpinion.progress)) {
            yield delay(5000);
            yield put(removeActiveMLOpinion(activeOpinion.opinionId));
        } else {
            const updatedMLOpinions: ActiveMLOpinion[] = yield select(getActiveMLOpinions);
            const progress = updatedMLOpinions.find(({ opinionId }) => opinionId === activeOpinion.opinionId)?.progress || 0;
            const shouldIncrementProgress = checkShouldIncrementProgress(opinionProgressSteps, progress);
            if (shouldIncrementProgress) {
                const randomIncrement = Math.floor((Math.random() + 1) * 2000);
                yield delay(randomIncrement);
                yield put(triggerProgressActiveMLOpinions(set('progress', progress + 1, activeOpinion)));
            }
        }
    }
}

function* activeMLOpinionProgressWatcher() {
    yield takeEvery(NotificationActionTypes.TRIGGER_PROGRESS_PERCENTAGE_ACTIVE_ML_OPINION, attemptUpdateActiveMLOpinionProgress);
}

export function* attemptTriggerAllActiveMLOpinionProgress({ payload }: ReturnType<typeof setActiveMLOpinions>) {
    const directFromSocket = true;
    for (const activeMLOpinion of payload) {
        yield put(triggerProgressActiveMLOpinions(set('progress', activeMLOpinion.progress + 1, activeMLOpinion), directFromSocket));
    }
}

function* setAllActiveMLOpinionProgressWatcher() {
    yield takeEvery(NotificationActionTypes.SET_ACTIVE_ML_OPINIONS, attemptTriggerAllActiveMLOpinionProgress);
}

export function* attemptUpdateTestMLDocumentProgress({ payload }: ReturnType<typeof triggerProgressTestMLDocuments>) {
    const { directFromSocket, testMLDocument } = payload;
    let testDocument = testMLDocument;
    const testMLDocuments: TestMLDocument[] = yield select(getTestMLDocuments);
    const currentProgress = getOr(1, 'progress', testMLDocuments.find(({ documentId }) => documentId === testDocument.documentId));
    if (((testDocument.progress > currentProgress) || testDocument.progress === 0) && currentProgress !== 0) {
        if (directFromSocket) {
            testDocument = setTestDocumentProgressString(testDocument);
        }
        yield put(updatePercentageTestMLDocument(testDocument));
        if ([100, 0].includes(testDocument.progress)) {
            yield delay(5000);
            yield put(removeTestMLDocument(testDocument.documentId));
        } else {
            const updatedTestMLDocuments: TestMLDocument[] = yield select(getTestMLDocuments);
            const progress = updatedTestMLDocuments.find(({ documentId }) => documentId === testDocument.documentId)?.progress || 0;
            const shouldIncrementProgress = checkShouldIncrementProgress(initialProgressSteps, progress);
            if (shouldIncrementProgress) {
                const randomIncrement = Math.floor((Math.random() + 1) * 1000);
                yield delay(randomIncrement);
                yield put(triggerProgressTestMLDocuments(set('progress', progress + 1, testDocument)));
            }
        }
    }
}

export function* attemptSetDocumentQueryInProgress({ payload }: ReturnType<typeof triggerProgressActiveMLDocuments>) {
    const { activeMLDocument } = payload;
    const instanceDocumentId: number | null = yield select(getInstanceDocumentId);

    if (instanceDocumentId === activeMLDocument.documentId && activeMLDocument.progress === 100) {
        const instance: Instance | null = yield select(getDatasetInstance);
        const instanceTimeline: DatasetInstanceTimeline | null = yield select(getDatasetInstanceTimeline);
        const timelineInstance = instanceTimeline?.instances.find(timelineInstance => timelineInstance.datasetInstanceId === instance?.instance.datasetInstanceId);
        yield put(setIsQueryingDocumentWithAI());
        if (timelineInstance && instanceTimeline) {
            const preventRedirect = true;
            yield put(openTimelineInstanceStarted(timelineInstance, instanceTimeline.originalDocumentId, preventRedirect));
        }
    }
}

function* attemptUpdateTestMLDocumentProgressWatcher() {
    yield takeEvery(NotificationActionTypes.TRIGGER_PROGRESS_PERCENTAGE_TEST_ML_DOCUMENT, attemptUpdateTestMLDocumentProgress);
}

function* attemptSetDocumentQueryInProgressWatcher() {
    yield takeEvery(NotificationActionTypes.TRIGGER_PROGRESS_PERCENTAGE_ACTIVE_ML_DOCUMENT, attemptSetDocumentQueryInProgress);
}

export function* attemptSetOpinionQueryInProgress({ payload }: ReturnType<typeof triggerProgressActiveMLOpinions>) {
    const { activeMLOpinion } = payload;
    const instanceOpinionId: number | null = yield select(getCurrentInstanceOpinionId);

    if (instanceOpinionId === activeMLOpinion.opinionId && activeMLOpinion.progress === 100) {
        const timeline: TimelineOpinionInstance[] = yield select(getOpinionInstanceTimeline);
        // At the time of making this change, we currently only override the instance content after AI complete if it is an original point on the timeline as we do not extract amendments/updates
        const isOriginal = timeline.length > 0 && timeline[0].opinionId === instanceOpinionId;
        if (isOriginal) {
            yield put(openOpinionInstanceStarted());
        }
    }
}

function* attemptSetOpinionQueryInProgressWatcher() {
    yield takeEvery(NotificationActionTypes.TRIGGER_PROGRESS_PERCENTAGE_ACTIVE_ML_OPINION, attemptSetOpinionQueryInProgress);
}

export function* attemptUpdateActiveFewShotDocumentProgress({ payload }: ReturnType<typeof triggerProgressActiveFewShotDocuments>) {
    const { directFromSocket, activeFewShotDocument } = payload;
    let activeDocument = activeFewShotDocument;
    const initialFewShotDocuments: ActiveFewShotDocument[] = yield select(getActiveFewShotDocuments);
    const currentProgress = getOr(1, 'progress', initialFewShotDocuments.find(({ mlFewShotDocumentId }) => mlFewShotDocumentId === activeDocument.mlFewShotDocumentId));
    if (((activeDocument.progress > currentProgress) || activeDocument.progress === 0) && currentProgress !== 0) {
        if (directFromSocket) {
            activeDocument = setFewShotProgressString(activeDocument);
        }
        yield put(updatePercentageActiveFewShotDocument(activeDocument));
        if ([100, 0].includes(activeDocument.progress)) {
            yield delay(5000);
            yield put(removeActiveFewShotDocument(activeDocument.mlFewShotDocumentId));
        } else {
            const updatedFewShotDocuments: ActiveFewShotDocument[] = yield select(getActiveFewShotDocuments);
            const progress = updatedFewShotDocuments.find(({ mlFewShotDocumentId }) => mlFewShotDocumentId === activeDocument.mlFewShotDocumentId)?.progress || 0;
            const shouldIncrementProgress = checkShouldIncrementProgress(opinionProgressSteps, progress);
            if (shouldIncrementProgress) {
                const randomIncrement = Math.floor((Math.random() + 1) * 2000);
                yield delay(randomIncrement);
                yield put(triggerProgressActiveFewShotDocuments(set('progress', progress + 1, activeDocument)));
            }
        }
    }
}

function* activeFewShotDocumentProgressWatcher() {
    yield takeEvery(NotificationActionTypes.TRIGGER_PROGRESS_PERCENTAGE_ACTIVE_FEW_SHOT_DOCUMENT, attemptUpdateActiveFewShotDocumentProgress);
}

export function* attemptTriggerAllActiveFewShotDocumentProgress({ payload }: ReturnType<typeof setActiveFewShotDocuments>) {
    const directFromSocket = true;
    for (const activeFewShotDocument of payload) {
        yield put(triggerProgressActiveFewShotDocuments(set('progress', activeFewShotDocument.progress + 1, activeFewShotDocument), directFromSocket));
    }
}

function* setAllActiveFewShotDocumentProgressWatcher() {
    yield takeEvery(NotificationActionTypes.SET_ACTIVE_FEW_SHOT_DOCUMENTS, attemptTriggerAllActiveFewShotDocumentProgress);
}

export function* attemptHandleFewShotDocumentAIComplete({ payload }: ReturnType<typeof triggerProgressActiveFewShotDocuments>) {
    const { activeFewShotDocument } = payload;
    const mlFewShotDocument: MLFewShotDocument | null = yield select(getFewShotDocument);
    const fewShotDocuments: FewShotDocumentTable[] = yield select(getFewShotDocuments);
    if (activeFewShotDocument.progress === 100) {
        // Only trigger if the user is currently viewing the Few Shot Document or Few Shot Documents Table
        if ((!isNull(mlFewShotDocument) && mlFewShotDocument.mlFewShotDocumentId === activeFewShotDocument.mlFewShotDocumentId) || (fewShotDocuments.length < 20)) {
            const { document, queries }: { document: MLFewShotDocument; queries: FewShotDocumentQuery[]; } = yield call(getFewShotDocumentById, { mlFewShotDocumentId: activeFewShotDocument.mlFewShotDocumentId });
            if (!isNull(mlFewShotDocument) && mlFewShotDocument.mlFewShotDocumentId === activeFewShotDocument.mlFewShotDocumentId) {
                const documentUrl: string = yield select(getFewShotDocumentUrl);
                yield put(openFewShotDocumentSuccessful(documentUrl, document, queries));
            }
            // It is possible we may be triggering a refetch here when you have uploaded a few shot document Agreement Type X and are now looking at few shot documents table for Agreement Type Y and the refetch is not required
            if (fewShotDocuments.length < 20) {
                const pageNumber: number = yield select(getTablePageNumber);
                yield put(fetchFewShotDocumentsStarted(pageNumber));
            }
        }
    }
}

function* attemptHandleFewShotDocumentAICompleteWatcher() {
    yield takeEvery(NotificationActionTypes.TRIGGER_PROGRESS_PERCENTAGE_ACTIVE_FEW_SHOT_DOCUMENT, attemptHandleFewShotDocumentAIComplete);
}

export function* notificationSaga() {
    yield all([
        fork(dismissNotificationWatcher),
        fork(dismissAllNotificationsWatcher),
        fork(activeMLDocumentProgressWatcher),
        fork(setAllActiveMLDocumentProgressWatcher),
        fork(attemptUpdateTestMLDocumentProgressWatcher),
        fork(attemptSetDocumentQueryInProgressWatcher),
        fork(setAllActiveMLOpinionProgressWatcher),
        fork(activeMLOpinionProgressWatcher),
        fork(attemptSetOpinionQueryInProgressWatcher),
        fork(setAllActiveFewShotDocumentProgressWatcher),
        fork(activeFewShotDocumentProgressWatcher),
        fork(attemptHandleFewShotDocumentAICompleteWatcher)
    ]);
}
